// src/pages/RecibosEliminados.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import './GenerarTicket.css';
import './ListaIngresos.css';
import './ConfiguracionRecibos.css';
import ActionMenu from '../components/ActionMenu';

const RecibosEliminados = () => {
    const [recibosEliminados, setRecibosEliminados] = useState([]);
    const [filtroMes, setFiltroMes] = useState(new Date().getMonth() + 1);
    const [filtroAno, setFiltroAno] = useState(new Date().getFullYear());
    const [loading, setLoading] = useState(true);
    const [mostrarEgresos, setMostrarEgresos] = useState(false); // si es true muestra egresos, si es false ingresos
    const [showReceipt, setShowReceipt] = useState(false);
    const [reciboSeleccionado, setReciboSeleccionado] = useState(null);
    const [casas, setCasas] = useState([]);
    const [recibosConfig, setRecibosConfig] = useState(null);
    const receiptRef = useRef(null);

    const navigate = useNavigate();
    const { userData } = useAuth();
    const fraccionamientoId = userData?.fraccionamientoId;

    // Cargar recibos eliminados, casas y configuración de recibos
    useEffect(() => {
        if (!fraccionamientoId) return;
        setLoading(true);

        const cargarEliminados = async () => {
            try {
                // Cargar configuración de recibos
                try {
                    const snapFracc = await getDoc(doc(db, 'fraccionamientos', fraccionamientoId));
                    if (snapFracc.exists()) {
                        setRecibosConfig(snapFracc.data().configuracionRecibos || null);
                    }
                } catch (errCfg) {
                    console.warn('Error cargando configuracionRecibos:', errCfg);
                }

                // Cargar casas para datos de residentes en recibos de ingresos
                try {
                    const snapCasas = await getDocs(collection(db, 'fraccionamientos', fraccionamientoId, 'casas'));
                    setCasas(snapCasas.docs.map(d => ({ id: d.id, ...d.data() })));
                } catch (errCasas) {
                    console.warn('Error cargando casas:', errCasas);
                }

                // Cargar todos los ingresos y filtrar eliminados en el cliente
                const ingresosRef = collection(db, 'fraccionamientos', fraccionamientoId, 'ingresos');
                const ingresosSnap = await getDocs(ingresosRef);
                const ingresos = ingresosSnap.docs
                    .map(doc => ({ id: doc.id, tipo: 'ingreso', ...doc.data() }))
                    .filter(doc => doc.deleted === true);

                // Cargar todos los egresos y filtrar eliminados en el cliente
                const egresosRef = collection(db, 'fraccionamientos', fraccionamientoId, 'egresos');
                const egresosSnap = await getDocs(egresosRef);
                const egresos = egresosSnap.docs
                    .map(doc => ({ id: doc.id, tipo: 'egreso', ...doc.data() }))
                    .filter(doc => doc.deleted === true);

                // Combinar y filtrar por mes/año, según selector
                let todos = mostrarEgresos ? egresos : ingresos;
                todos = todos.filter(recibo => {
                    if (!recibo.deletedAt) return false;
                    const fecha = recibo.deletedAt.toDate ? recibo.deletedAt.toDate() : new Date(recibo.deletedAt);
                    return fecha.getMonth() + 1 === filtroMes && fecha.getFullYear() === filtroAno;
                });
                todos.sort((a, b) => {
                    const fechaA = a.deletedAt?.toDate ? a.deletedAt.toDate() : new Date(a.deletedAt);
                    const fechaB = b.deletedAt?.toDate ? b.deletedAt.toDate() : new Date(b.deletedAt);
                    return fechaB - fechaA;
                });

                setRecibosEliminados(todos);
                setLoading(false);
                setTimeout(() => {
                    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                }, 100);
            } catch (error) {
                console.error('Error cargando recibos eliminados:', error);
                setLoading(false);
            }
        };

        cargarEliminados();
    }, [fraccionamientoId, filtroMes, filtroAno, mostrarEgresos]);

    const formatCurrency = (amount) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount || 0);

    const exportarRecibos = () => {
        const datos = recibosEliminados.map(recibo => ({
            'Fecha Eliminación': new Date(recibo.deletedAt?.toDate ? recibo.deletedAt.toDate() : recibo.deletedAt).toLocaleDateString('es-MX'),
            'Tipo': recibo.tipo === 'ingreso' ? 'Ingreso' : 'Egreso',
            'Concepto': recibo.concepto || recibo.descripcion || 'N/A',
            'Monto': recibo.monto || 0,
            'Descuento': recibo.descuento || 0,
            'Neto': recibo.montoNeto || recibo.monto || 0,
            'Razón Eliminación': recibo.deletedReason || 'N/A'
        }));

        const ws = XLSX.utils.json_to_sheet(datos);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Recibos Eliminados');
        XLSX.writeFile(wb, `Recibos_Eliminados_${filtroMes}_${filtroAno}.xlsx`);
    };

    // Funciones auxiliares para renderizado de recibos de ingresos
    const numeroALetras = (num = 0) => {
        const unidades = ['CERO', 'UNO', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE'];
        const decenas = ['DIEZ', 'ONCE', 'DOCE', 'TRECE', 'CATORCE', 'QUINCE', 'DIECISÉIS', 'DIECISIETE', 'DIECIOCHO', 'DIECINUEVE'];
        const tens = ['', 'DIEZ', 'VEINTE', 'TREINTA', 'CUARENTA', 'CINCUENTA', 'SESENTA', 'SETENTA', 'OCHENTA', 'NOVENTA'];
        const centenas = ['', 'CIENTO', 'DOSCIENTOS', 'TRESCIENTOS', 'CUATROCIENTOS', 'QUINIENTOS', 'SEISCIENTOS', 'SETECIENTOS', 'OCHOCIENTOS', 'NOVECIENTOS'];
        const toWords = (n) => {
            let w = '';
            const c = Math.floor(n / 100); const d = Math.floor((n % 100) / 10); const u = n % 10;
            if (c) w += (c === 1 && d === 0 && u === 0 ? 'CIEN' : centenas[c]) + ' ';
            if (d === 1) w += decenas[u] + ' ';
            else if (d === 2) w += (u === 0 ? 'VEINTE' : 'VEINTI' + unidades[u].toLowerCase()) + ' ';
            else if (d > 2) { w += tens[d]; if (u) w += ' Y ' + unidades[u]; w += ' '; }
            else if (u) w += unidades[u] + ' ';
            return w.trim();
        };
        const miles = Math.floor(num / 1000); const resto = num % 1000;
        let res = '';
        if (miles) res += (miles === 1 ? 'MIL' : toWords(miles) + ' MIL') + ' ';
        if (resto || num === 0) res += toWords(resto);
        return res.trim();
    };

    const toPublicUrl = (url) => {
        if (!url || typeof url !== 'string') return '';
        try {
            const p = new URL(url);
            if (!p.hostname.includes('firebasestorage')) return url;
            p.search = '?alt=media';
            return p.toString();
        } catch {
            return url.split('?')[0] + '?alt=media';
        }
    };

    const renderReceipt = (ingreso) => {
        const casa = casas.find(c => c.id === ingreso.casaId) || {};
        const cfg = recibosConfig || {};
        const formatMoney = (n) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: cfg.moneda || 'MXN' }).format(n || 0);
        const total = ingreso.montoNeto || ingreso.monto || 0;
        const totalLetras = `${numeroALetras(Math.floor(total))} ${cfg.moneda === 'USD' ? 'DÓLARES' : 'PESOS MEXICANOS'} ${(total % 1).toFixed(2).split('.')[1] || '00'}/100`;

        return (
            <div className="receipt-preview">
                <div className="preview-row preview-top">
                    <div>
                        {cfg.logoUrl ? <img src={toPublicUrl(cfg.logoUrl)} alt="Logo" className="preview-logo" /> : <div className="preview-logo placeholder">Logo</div>}
                        <div className="preview-condo"><h4>{cfg.condominioNombre || 'Condominio'}</h4><p>{cfg.condominioDireccion || ''}</p></div>
                    </div>
                    <div className="preview-right">
                        <h3 className="preview-title">RECIBO DE PAGO</h3>
                        <div className="preview-meta">
                            <span>Folio: {ingreso.folio || '---'}</span>
                            <span>Fecha de pago: {ingreso.fechaPagoOriginal ? (() => {
                                const [y, m, d] = ingreso.fechaPagoOriginal.split('T')[0].split('-');
                                return `${d}/${m}/${y}`;
                            })() : ingreso.fecha?.toDate?.().toLocaleDateString('es-MX')}</span>
                            <span>Fecha de emisión: {ingreso.fecha?.toDate?.().toLocaleDateString('es-MX')} {ingreso.fecha?.toDate?.().toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                    </div>
                </div>
                <hr className="preview-divider" />
                <div className="preview-block">
                    <h5>Datos de Facturación</h5>
                    <p><strong>Razón Social:</strong> {cfg.razonSocial || '---'}</p>
                    <p><strong>RFC:</strong> {cfg.rfc || '---'}</p>
                    {cfg.datosFacturacion && <p className="preview-muted">{cfg.datosFacturacion}</p>}
                </div>
                <hr className="preview-divider" />
                <div className="preview-block">
                    <h5>Administrador</h5>
                    <p><strong>Nombre:</strong> {cfg.administradorNombre || '---'}</p>
                    <p><strong>Dirección:</strong> {cfg.adminDireccion || '---'}</p>
                </div>
                <hr className="preview-divider" />
                <div className="preview-block">
                    <h5>Residente</h5>
                    <p><strong>Casa:</strong> {casa.numero || ingreso.casaNumero || '---'}</p>
                    <p><strong>Nombre:</strong> {casa.propietario || casa.nombre_residente || ingreso.nombreResidente || '---'}</p>
                    <p><strong>Email:</strong> {casa.email || '---'}</p>
                    <p><strong>Teléfono:</strong> {casa.telefono || '---'}</p>
                </div>
                <hr className="preview-divider" />
                <div className="preview-metodo-pago">
                    <div className="preview-block">
                        <p><strong>Método de Pago:</strong> {ingreso.metodoPago || '---'}</p>
                        <p><strong>Referencia:</strong> {ingreso.referencia || '---'}</p>
                    </div>
                </div>
                <hr className="preview-divider" />
                <div className="preview-block">
                    <h5>Conceptos de Pago</h5>
                    <div className="preview-table">
                        <div className="preview-table-header"><span>Concepto</span><span>Importe</span></div>
                        {(() => {
                            const partidasArray = (Array.isArray(ingreso.pagos) && ingreso.pagos.length > 0)
                                ? ingreso.pagos
                                : (Array.isArray(ingreso.desgloseConceptos) && ingreso.desgloseConceptos.length > 0)
                                    ? ingreso.desgloseConceptos
                                    : null;

                            if (partidasArray) {
                                return partidasArray.map((item, idx) => {
                                    let detalleText = item.detalle || item.mesSeleccionadoTexto || '';
                                    if (!detalleText && Array.isArray(item.desgloseMensual) && item.desgloseMensual.length > 0) {
                                        detalleText = item.desgloseMensual.map(d => `${d.mes} ${d.año || d.anio || ''}${d.esParcial ? ' (Parcial)' : ''}`).join(', ');
                                    }
                                    return (
                                        <div key={idx} className="preview-table-row">
                                            <span>{item.concepto} {detalleText ? `· ${detalleText}` : ''}</span>
                                            <span>{formatMoney(item.montoNeto || item.monto)}</span>
                                        </div>
                                    );
                                });
                            }

                            if (Array.isArray(ingreso.desgloseMensual) && ingreso.desgloseMensual.length > 0) {
                                const nombreConcepto = ingreso.concepto || 'Cuota de Mantenimiento';
                                return ingreso.desgloseMensual.map((item, idx) => {
                                    const anioStr = item.año || item.anio || item.year || '';
                                    const sufijoEstado = item.esParcial ? ' · Pagado Parcial' : ' · Pagado';
                                    return (
                                        <div key={idx} className="preview-table-row">
                                            <span>{nombreConcepto} · {item.mes} {anioStr}{sufijoEstado}</span>
                                            <span>{formatMoney(item.importe)}</span>
                                        </div>
                                    );
                                });
                            }

                            return (
                                <div className="preview-table-row">
                                    <span>{ingreso.concepto}</span>
                                    <span>{formatMoney(ingreso.monto)}</span>
                                </div>
                            );
                        })()}
                    </div>
                </div>

                {ingreso.observaciones && ingreso.observaciones.trim() && (
                    <>
                        <hr className="preview-divider" />
                        <div className="preview-block">
                            <h5>Observaciones</h5>
                            <p style={{ fontSize: '13px', color: '#374151', whiteSpace: 'pre-wrap' }}>{ingreso.observaciones}</p>
                        </div>
                    </>
                )}

                {(() => {
                    const aplicadoAIntereses = Number(ingreso.aplicadoAIntereses || 0);
                    const aplicadoACapital = Number(ingreso.aplicadoACapital || 0);
                    const aplicadoAAgua = Number(ingreso.aplicadoAAgua || 0);
                    const aplicadoAExtras = Number(ingreso.aplicadoAExtras || 0);
                    const aplicadoAMultas = Number(ingreso.aplicadoAMultas || 0);
                    const adeudoNuevo = Number(ingreso.adeudoNuevo || 0);
                    const saldoFavorNuevo = Number(ingreso.saldoFavorNuevo || 0);

                    const tieneDesglose = aplicadoAIntereses > 0 || aplicadoACapital > 0 || aplicadoAAgua > 0 || aplicadoAExtras > 0 || aplicadoAMultas > 0 || adeudoNuevo > 0 || saldoFavorNuevo > 0;
                    if (!tieneDesglose) return null;
                    return (
                        <>
                            <hr className="preview-divider" />
                            <div className="preview-block">
                                <h5>Aplicación del Pago</h5>
                                <div className="preview-table">
                                    <div className="preview-table-header"><span>Detalle</span><span>Monto</span></div>
                                    {aplicadoAIntereses > 0 && <div className="preview-table-row"><span>Pago a intereses</span><span>{formatMoney(aplicadoAIntereses)}</span></div>}
                                    {aplicadoACapital > 0 && <div className="preview-table-row"><span>Pago a capital</span><span>{formatMoney(aplicadoACapital)}</span></div>}
                                    {aplicadoAAgua > 0 && <div className="preview-table-row"><span>Pago a Agua Potable</span><span>{formatMoney(aplicadoAAgua)}</span></div>}
                                    {aplicadoAExtras > 0 && <div className="preview-table-row"><span>Pago a Cuotas Extraordinarias</span><span>{formatMoney(aplicadoAExtras)}</span></div>}
                                    {aplicadoAMultas > 0 && <div className="preview-table-row"><span>Pago a Sanciones Admin.</span><span>{formatMoney(aplicadoAMultas)}</span></div>}
                                    {saldoFavorNuevo > 0 && (
                                        <div className="preview-table-row" style={{ color: '#059669', fontWeight: 700 }}>
                                            <span>Saldo a favor restante</span>
                                            <span>{formatMoney(saldoFavorNuevo)}</span>
                                        </div>
                                    )}
                                    {adeudoNuevo > 0 && (
                                        <div className="preview-table-row" style={{ color: '#b91c1c', fontWeight: 700 }}>
                                            <span>Adeudo total restante</span>
                                            <span>{formatMoney(adeudoNuevo)}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    );
                })()}

                <hr className="preview-divider" />
                <div className="preview-total"><span>TOTAL</span><span className="preview-total-amount">{formatMoney(total)}</span></div>
                <p className="preview-leyenda" style={{ fontSize: '10px', textTransform: 'uppercase', fontWeight: '600' }}>SON: {totalLetras}</p>

                <div className="preview-footer">
                    <div className="preview-firma-stack">
                        {cfg.selloUrl && <img src={toPublicUrl(cfg.selloUrl)} alt="Sello" className="preview-sello" />}
                        <div className="preview-firma"><div className="preview-firma-line" /><small>Firma y Sello</small><span>{cfg.administradorNombre || '---'}</span></div>
                    </div>
                </div>
            </div>
        );
    };

    // Constructor de recibo HTML para egresos
    const buildEgresoReceiptHtml = (egreso) => {
        const cfg = recibosConfig || {};
        const folio = egreso.folio || '---';
        const fechaPago = egreso.fecha?.toDate ? egreso.fecha.toDate() : (egreso.fecha ? new Date(egreso.fecha) : new Date());
        const fechaEmision = egreso.timestamp?.toDate ? egreso.timestamp.toDate() : new Date();
        const moneda = cfg.moneda || 'MXN';
        const formatMoney = (val) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: moneda }).format(val || 0);
        const descuentoLinea = Number(egreso.descuentoPorcentaje || 0) > 0;
        const monedaEtiqueta = (moneda || 'MXN').toUpperCase() === 'USD' ? 'DÓLARES ESTADOUNIDENSES' : 'PESOS MEXICANOS';
        const totalNumero = Math.round(egreso.montoNeto || egreso.monto || 0);
        const totalEnLetras = `${numeroALetras(totalNumero)} ${monedaEtiqueta} 00/100`;

        return `
            <div class="receipt-preview" style="max-width: 800px; margin: 0 auto; background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 24px; font-family: Arial, sans-serif;">
                <div style="display: flex; justify-content: space-between; gap: 16px; align-items: flex-start;">
                    <div style="display: flex; gap: 14px; align-items: center;">
                        <div style="width: 80px; height: 80px; border: 1px dashed #cbd5e1; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-weight: 700; color: #94a3b8; overflow: hidden;">
                            ${cfg.logoUrl ? `<img src="${toPublicUrl(cfg.logoUrl)}" alt="Logo" style="width: 100%; height: 100%; object-fit: contain;" />` : 'Logo'}
                        </div>
                        <div>
                            <div style="font-weight: 800; font-size: 17px; color: #111827;">${cfg.condominioNombre || 'Residencial / Condominio'}</div>
                            <div style="color: #6b7280; font-size: 12px;">${cfg.condominioDireccion || ''}</div>
                        </div>
                    </div>
                    <div style="text-align: right;">
                        <h3 style="margin: 0; font-size: 18px; color: #111827; font-weight: 800;">RECIBO DE EGRESO</h3>
                        <div style="font-size: 13px; color: #4b5563; margin-top: 4px;">
                            <div>Folio: <strong>${folio}</strong></div>
                            <div>Fecha de egreso: ${fechaPago.toLocaleDateString('es-MX')}</div>
                            <div>Fecha de emisión: ${fechaEmision.toLocaleDateString('es-MX')} ${fechaEmision.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}</div>
                        </div>
                    </div>
                </div>

                <hr style="margin: 16px 0; border: 0; border-top: 1px solid #e5e7eb;" />

                <div style="margin-top: 12px;">
                    <h5 style="margin: 0 0 4px; font-size: 13px; color: #111827; text-transform: uppercase;">Proveedor</h5>
                    <p style="margin: 2px 0; font-size: 13px; color: #374151;"><strong>Nombre:</strong> ${egreso.proveedor || 'Proveedor'}</p>
                    <p style="margin: 2px 0; font-size: 13px; color: #374151;"><strong>Referencia / Factura:</strong> ${egreso.referenciaFactura || egreso.referencia || 'N/A'}</p>
                    ${egreso.metodoPago ? `<p style="margin: 2px 0; font-size: 13px; color: #374151;"><strong>Método de Pago:</strong> ${egreso.metodoPago}</p>` : ''}
                </div>

                <hr style="margin: 16px 0; border: 0; border-top: 1px solid #e5e7eb;" />

                <div style="margin-top: 12px;">
                    <h5 style="margin: 0 0 8px; font-size: 13px; color: #111827; text-transform: uppercase;">Conceptos de Egreso</h5>
                    <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
                        <thead>
                            <tr style="background: #f8fafc; text-align: left;">
                                <th style="border: 1px solid #e5e7eb; padding: 8px;">Concepto</th>
                                <th style="border: 1px solid #e5e7eb; padding: 8px; text-align: right;">Importe</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td style="border: 1px solid #e5e7eb; padding: 8px;">${egreso.descripcion || egreso.categoria || 'Concepto'}</td>
                                <td style="border: 1px solid #e5e7eb; padding: 8px; text-align: right;">${formatMoney(egreso.monto)}</td>
                            </tr>
                            ${descuentoLinea ? `
                            <tr>
                                <td style="border: 1px solid #e5e7eb; padding: 8px;">Descuento (${egreso.descuentoPorcentaje}%)</td>
                                <td style="border: 1px solid #e5e7eb; padding: 8px; text-align: right; color: #dc2626;">-${formatMoney(egreso.descuento || 0)}</td>
                            </tr>` : ''}
                        </tbody>
                    </table>
                </div>

                <div style="display: flex; justify-content: space-between; align-items: flex-end; margin-top: 16px; font-size: 15px; font-weight: 700; color: #111827; border-top: 1px solid #e5e7eb; padding-top: 10px;">
                    <span>TOTAL</span>
                    <span style="font-size: 18px; color: #dc2626;">${formatMoney(egreso.montoNeto || egreso.monto)}</span>
                </div>
                <p style="font-size: 10px; text-transform: uppercase; font-weight: 600; color: #6b7280; margin-top: 6px;">SON: ${totalEnLetras}</p>

                <div style="margin-top: 36px; text-align: center;">
                    <div style="display: inline-block; min-width: 200px; text-align: center;">
                        <div style="border-top: 1px solid #cbd5e1; margin-bottom: 4px;"></div>
                        <small style="font-size: 11px; color: #6b7280;">Firma de Autorización</small>
                        <div style="font-weight: 600; font-size: 13px; color: #1f2937;">${cfg.administradorNombre || 'Administración'}</div>
                    </div>
                </div>
            </div>
        `;
    };

    // Descargar o imprimir recibo
    const handleDownloadPrint = () => {
        if (receiptRef.current) {
            const w = window.open('', '_blank');
            const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
                .map(style => style.outerHTML)
                .join('');

            w.document.write(`
                <html>
                    <head>
                        <title>Recibo Eliminado - ${reciboSeleccionado?.folio || ''}</title>
                        <meta charset="utf-8" />
                        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                        ${styles}
                        <style>
                            body { background-color: white; margin: 0; padding: 20px; font-family: Arial, sans-serif; }
                            @media print {
                                body { padding: 0; }
                                .no-print { display: none !important; }
                            }
                        </style>
                    </head>
                    <body>
                        <div style="display: flex; justify-content: center;">
                            ${receiptRef.current.innerHTML}
                        </div>
                        <script>
                            window.onload = function() {
                                setTimeout(function() {
                                    window.print();
                                }, 500);
                            };
                        </script>
                    </body>
                </html>
            `);
            w.document.close();
        }
    };

    return (
        <div className="generar-ticket-page">
            <div className="lista-egresos-header">
                <button onClick={() => navigate('/finanzas')} className="btn-back-simple">← Volver</button>
                <h2>Recibos Eliminados</h2>
                <button onClick={exportarRecibos} className="btn-export-simple" style={{ background: '#10b981', color: 'white', padding: '10px 15px', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
                    📊 Exportar
                </button>
            </div>

            <div className="filtros-container-recibos-eliminados">
                <div className="filtro-grupo">
                    <label>Mes</label>
                    <select value={filtroMes} onChange={(e) => setFiltroMes(parseInt(e.target.value))}>
                        <option value="1">Enero</option>
                        <option value="2">Febrero</option>
                        <option value="3">Marzo</option>
                        <option value="4">Abril</option>
                        <option value="5">Mayo</option>
                        <option value="6">Junio</option>
                        <option value="7">Julio</option>
                        <option value="8">Agosto</option>
                        <option value="9">Septiembre</option>
                        <option value="10">Octubre</option>
                        <option value="11">Noviembre</option>
                        <option value="12">Diciembre</option>
                    </select>
                </div>
                <div className="filtro-grupo">
                    <label>Año</label>
                    <select value={filtroAno} onChange={(e) => setFiltroAno(parseInt(e.target.value))}>
                        {[...Array(5)].map((_, i) => {
                            const year = new Date().getFullYear() - i;
                            return <option key={year} value={year}>{year}</option>;
                        })}
                    </select>
                </div>
                <div className="filtro-grupo checkbox">
                    <label>
                        <input
                            type="checkbox"
                            checked={mostrarEgresos}
                            onChange={(e) => setMostrarEgresos(e.target.checked)}
                        />
                        {mostrarEgresos ? 'Egresos Eliminados' : 'Ingresos Eliminados'}
                    </label>
                </div>
            </div>

            {loading ? (
                <p style={{ textAlign: 'center' }}>Cargando recibos eliminados...</p>
            ) : recibosEliminados.length > 0 ? (
                <div className="tabla-ingresos" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                    <table>
                        <thead>
                            <tr>
                                <th>Fecha Eliminación</th>
                                <th>Tipo</th>
                                <th>Concepto</th>
                                <th>Monto</th>
                                <th>Descuento</th>
                                <th>Neto</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {recibosEliminados.map((recibo) => {
                                const fecha = recibo.deletedAt?.toDate ? recibo.deletedAt.toDate() : new Date(recibo.deletedAt);
                                const dateLabel = fecha.toLocaleDateString('es-MX');
                                const timeLabel = fecha.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });

                                return (
                                    <tr key={`${recibo.tipo}-${recibo.id}`}>
                                        <td>
                                            {dateLabel}
                                            <div style={{ fontSize: '0.82rem', color: '#6b7280', marginTop: '2px' }}>{timeLabel}</div>
                                        </td>
                                        <td style={{ fontWeight: '600', color: recibo.tipo === 'ingreso' ? '#059669' : '#dc2626' }}>
                                            {recibo.tipo === 'ingreso' ? '📥 Ingreso' : '📤 Egreso'}
                                        </td>
                                        <td>{recibo.concepto || recibo.descripcion || 'N/A'}</td>
                                        <td className="monto-ingreso">{formatCurrency(recibo.monto)}</td>
                                        <td className="descuento-info">{recibo.descuentoPorcentaje ? `${recibo.descuentoPorcentaje}% (-${formatCurrency(recibo.descuento)})` : 'N/A'}</td>
                                        <td className="monto-neto">{formatCurrency(recibo.montoNeto || recibo.monto)}</td>
                                        <td>
                                            <ActionMenu
                                                onView={() => {
                                                    setReciboSeleccionado(recibo);
                                                    setShowReceipt(true);
                                                }}
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            ) : (
                <p style={{ textAlign: 'center', color: '#666', marginTop: '2rem' }}>No hay recibos eliminados para el período seleccionado.</p>
            )}

            {/* Modal para ver el recibo eliminado exactamente como se emitió sin restaurarlo */}
            {showReceipt && reciboSeleccionado && (
                <div className="modal-overlay receipt-modal" onClick={() => setShowReceipt(false)}>
                    <div className="modal-content receipt-modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close-btn" onClick={() => setShowReceipt(false)} title="Cerrar vista previa">✕</button>

                        <div style={{
                            background: '#fef2f2',
                            border: '2px dashed #f87171',
                            color: '#991b1b',
                            padding: '10px 16px',
                            borderRadius: '8px',
                            marginBottom: '14px',
                            textAlign: 'center',
                            fontWeight: 700,
                            fontSize: '0.88rem'
                        }}>
                            ⚠️ RECIBO ELIMINADO — Eliminado el {reciboSeleccionado.deletedAt?.toDate ? reciboSeleccionado.deletedAt.toDate().toLocaleDateString('es-MX') : new Date(reciboSeleccionado.deletedAt || Date.now()).toLocaleDateString('es-MX')} {reciboSeleccionado.deletedAt?.toDate ? reciboSeleccionado.deletedAt.toDate().toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : ''}
                            {reciboSeleccionado.deletedReason ? ` · Motivo: ${reciboSeleccionado.deletedReason}` : ''}
                        </div>

                        <div className="receipt-scroll-area">
                            <div className="receipt-preview-container">
                                <div className="preview-wrapper" ref={receiptRef}>
                                    {reciboSeleccionado.tipo === 'ingreso' ? (
                                        renderReceipt(reciboSeleccionado)
                                    ) : (
                                        <div dangerouslySetInnerHTML={{ __html: buildEgresoReceiptHtml(reciboSeleccionado) }} />
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="receipt-actions">
                            <button type="button" className="btn-secondary btn-download" onClick={handleDownloadPrint}>
                                ⬇️ Descargar / Imprimir
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RecibosEliminados;
